import { ErrorToast, SuccessToast } from '@services/utils/ToastUtils';
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { NotesCountMap, NoteView } from '../../../../types/Note';
import { CrmType, getCrmName, isCrmReactive } from './crmData';
import NoteComponent from './NoteComponent';
import NoteInput from './NoteInput';
import NoteLoading from './NoteLoading';

type NoteProps = {
  fullName: string;
  dealerId: string;
  leadId: string;
  vin: string;
  isNotesModalOpen: boolean;
  notesCountMap: NotesCountMap[];
  noteInput: string;
  openRetryInfo?: (note: NoteView) => void;
  getNotes: (leadId: string, dealerId: string) => Promise<NoteView[]>;
  postNotes: (
    leadId: string,
    noteInput: string,
    dealerId: string,
    vin?: string,
  ) => Promise<NoteView>;
  updateNotesCount: (value: NotesCountMap) => void;
  setNoteInput: React.Dispatch<React.SetStateAction<string>>;
  retry: (id: string | number, dealerId: string, addMissingData: boolean) => Promise<void>;
};

const Notes: FC<NoteProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSavingNote, setIsSavingNote] = useState<boolean>(false);
  const [isRetryInProgress, setIsRetryInProgress] = useState<boolean>(false);
  const [savingNoteCrmType, setSavingNoteCrmType] = useState<CrmType | undefined>(undefined);
  const [notes, setNotes] = useState<Array<NoteView>>([]);
  const [newNotesIds, setNewNotesIds] = useState<number[]>([]);
  const [pendingNotesIds, setPendingNotesIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let mounted = true;
    if (props.isNotesModalOpen) {
      setNewNotesIds([]);
      if (notesCount > 0) {
        setIsLoading(true);
      }
      props
        .getNotes(props.leadId, props.dealerId)
        .then((res) => {
          if (mounted) {
            res.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            setNotes(res);
          }
        })
        .catch((error) => {
          console.log(error);
          if (mounted) {
            ErrorToast('An error has occurred while getting notes.');
          }
        })
        .finally(() => {
          if (mounted) {
            setIsLoading(false);
          }
        });
    } else {
      setNotes([]);
      setNewNotesIds([]);
      setIsLoading(true);
    }
    return () => {
      mounted = false;
    };
  }, [props.isNotesModalOpen, setIsLoading, setNotes, props.setNoteInput]);

  const placeholderText = `Add a note about ${props.fullName}...`;

  const onRetryClick = (
    id: string | number,
    dealerId: string,
    addMissingData: boolean,
    crmType: CrmType,
  ) => {
    setSavingNoteCrmType(crmType);
    if (isCrmReactive(crmType)) {
      setIsRetryInProgress(true);
    }
    props.retry(id, dealerId, addMissingData);
  };

  useEffect(() => {
    setIsSaving((pendingNotesIds.size > 0 && newNotesIds.length > 0) || isSavingNote);
  }, [isLoading, pendingNotesIds, isSavingNote, setIsSaving]);

  const handleSaveClick = async () => {
    if (
      props.noteInput.length > 0 &&
      props.noteInput !== placeholderText &&
      isSavingNote === false
    ) {
      setIsSavingNote(true);
      props
        .postNotes(props.leadId, props.noteInput, props.dealerId, props.vin)
        .then((response) => {
          const existingNotes = [...notes];
          setNewNotesIds([...newNotesIds, Number(response.id)]);
          existingNotes.unshift(response);
          setNotes(existingNotes);
          SuccessToast(response.content);
          props.setNoteInput('');
          props.updateNotesCount({ count: notesCount + 1, leadId: props.leadId });
          setIsSavingNote(false);
        })
        .catch(() => {
          setIsSavingNote(false);
          ErrorToast('An error has occurred when pushing a note');
        });
    }
  };

  const handleNoteChanged = (e: ChangeEvent<HTMLTextAreaElement>) => {
    props.setNoteInput(e.target.value);
  };
  const updateSingleNoteInNotes = (newItem: NoteView, id: number) => {
    setNotes(notes.map((item) => (item.id === id ? { ...newItem } : item)));
  };

  const addPendingNoteId = useCallback((id: number) => {
    setPendingNotesIds((prev) => {
      const newIdsSet = new Set(prev);
      newIdsSet.add(id);
      return newIdsSet;
    });
  }, []);

  const removePendingNoteId = useCallback((id: number) => {
    setPendingNotesIds((prev) => {
      const newIdsSet = new Set(prev);
      newIdsSet.delete(id);
      return newIdsSet;
    });
  }, []);

  const resetRetryState = useCallback(() => {
    setIsRetryInProgress(false);
    setSavingNoteCrmType(undefined);
  }, []);

  const setSavingNoteCrmTypeAction = useCallback((crmType: CrmType | undefined) => {
    setSavingNoteCrmType(crmType);
  }, []);

  const removeNewNote = (id: number) => {
    setNewNotesIds((prev) => {
      const newIdsSet = [...prev];
      return newIdsSet.filter((noteId) => id !== noteId);
    });
  };
  const savingNoteText = savingNoteCrmType
    ? `Sending note to ${getCrmName(savingNoteCrmType)}`
    : 'Saving note ';
  const notesCount = props.notesCountMap?.find((x) => x.leadId === props.leadId)?.count;
  const showLoadingScreen =
    isLoading || (pendingNotesIds.size > 0 && !isSaving && !isRetryInProgress);
  return (
    <NotesContentContainer>
      {(isSaving || isRetryInProgress) && <NoteLoading text={savingNoteText} coverModal />}
      <NoteInput
        handleNoteChanged={handleNoteChanged}
        handleSaveClick={handleSaveClick}
        noteInput={props.noteInput}
        placeholderText={placeholderText}
      />
      <NotesWrapper>
        {showLoadingScreen && <NoteLoading text='Loading data' coverModal={false} />}
        {!showLoadingScreen && notes && notes.length > 0 && (
          <NoteWrapper data-testid='note-body'>
            {notes.map((note) => {
              return (
                <NoteComponent
                  key={note.id}
                  id={note.id}
                  content={note.content}
                  createdAt={note.createdAt}
                  offerNotePushRetry={note.offerNotePushRetry}
                  source={note.source}
                  warning={note.warning}
                  createdBy={note.createdBy}
                  crmType={note?.crmType}
                  crmStatus={note?.crmStatus}
                  isNewlyAdded={newNotesIds.some((el) => el === note.id)}
                  openRetryInfo={() => props.openRetryInfo(note)}
                  dealerId={props.dealerId}
                  updateSingleNote={updateSingleNoteInNotes}
                  addPendingNoteId={addPendingNoteId}
                  removePendingNoteId={removePendingNoteId}
                  resetRetryState={resetRetryState}
                  retryAction={onRetryClick}
                  removeNewNote={removeNewNote}
                  setSavingNoteCrmType={setSavingNoteCrmTypeAction}
                />
              );
            })}
          </NoteWrapper>
        )}
      </NotesWrapper>
    </NotesContentContainer>
  );
};

export default Notes;

const NotesWrapper = styled.div`
  overflow: auto;
  width: 100%;
  min-width: fit-content;
  max-height: 500px;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
`;

const NotesContentContainer = styled.div`
  position: relative;
`;

const NoteWrapper = styled.div`
  min-width: fit-content;
`;
